import { SessionSource } from '../types/superadmin';

/** Small formatting helpers shared by the session list and the review page. */

const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;

/** '1h 04m' / '12m 30s' / '45s'. */
export function formatDuration(seconds: number): string {
  const total = Math.max(0, Math.round(seconds || 0));
  const hours = Math.floor(total / SECONDS_PER_HOUR);
  const minutes = Math.floor((total % SECONDS_PER_HOUR) / SECONDS_PER_MINUTE);
  const rest = total % SECONDS_PER_MINUTE;

  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${String(rest).padStart(2, '0')}s`;
  return `${rest}s`;
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * '7 Aug 2026, 15:18' — UTC, matching how sessions are stored and how every
 * range on this portal is bucketed. Rendering these in the viewer's local zone
 * would put a call in a different day from the chart bar that counts it.
 */
export function formatSessionTime(iso: string | null): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const time = `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`;
  return `${date.getUTCDate()} ${MONTHS[date.getUTCMonth()]} ${date.getUTCFullYear()}, ${time}`;
}

const SOURCE_LABELS: Record<SessionSource, string> = {
  calendar: 'Calendar bot',
  manual: 'Manual bot',
  standalone_desktop: 'Desktop app',
  historical: 'Historical upload'
};

const PLATFORM_LABELS: Record<string, string> = {
  macos: 'macOS',
  windows: 'Windows'
};

/**
 * How the call was captured. Unrecognised sources render verbatim rather than
 * as "Unknown", so a capture mode added after this file is still legible.
 */
export function sourceLabel(source: string | null, platform?: string | null): string {
  const base = source ? SOURCE_LABELS[source as SessionSource] ?? source : 'Unknown source';
  const suffix = platform ? PLATFORM_LABELS[platform] ?? platform : null;
  return suffix ? `${base} · ${suffix}` : base;
}

/** Tone for the session's processing status chip. */
export function statusTone(status: string): string {
  if (status === 'completed') return 'pos';
  if (status === 'active') return 'warn';
  return 'neg';
}

/** Tone for the post-call analysis status chip. */
export function analysisTone(status: string | null): string {
  if (status === 'complete') return 'pos';
  if (status === 'pending') return 'warn';
  if (status === 'failed') return 'neg';
  return '';
}

/** Human label for the analysis status, including "never ran". */
export function analysisLabel(status: string | null): string {
  if (!status) return 'not analyzed';
  return status;
}
